import { IReportIntrospection, Option } from '@common/types';
import { ALL_OPTION } from '@common/types/constants.ts';
import {
  Box,
  Checkbox,
  TextField,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { theme } from '@store';
import { ChangeEvent, useState } from 'react';
import { useReportStore } from '@store';
import { Search } from '@mui/icons-material';

//This is where the fields that we want to display in the table are selected.
//They are separated by entity type into groups.
export const Fields = () => {
  const { introspection } = useReportStore();
  const [search, setSearch] = useState<string>('');

  const handleSearchOnChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setSearch(e.target.value);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        p: '1rem',
        flex: 1,
        bgcolor: theme.palette.grey[50],
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Typography variant='body1'>
        Select the fields to include in the report.
      </Typography>
      <TextField
        value={search}
        onChange={handleSearchOnChange}
        placeholder='Search fields'
        sx={{
          bgcolor: 'white',
        }}
        slotProps={{
          input: {
            startAdornment: (
              <Search sx={{ mr: 1, color: 'rgba(0, 0, 0, 0.56)' }} />
            ),
          },
        }}
      />
      <Box
        sx={{
          overflowX: 'auto',
          maxHeight: 'calc(100vh - 350px)',
        }}
      >
        {introspection?.groups?.map((section, index) => (
          <FieldsSection
            key={`${section.group}-${index}`}
            section={section}
            search={search}
          />
        ))}
      </Box>
    </Box>
  );
};

interface IFieldsSectionProps {
  section: IReportIntrospection['groups'][0];
  search: string;
}

const FieldsSection = ({ section, search }: IFieldsSectionProps) => {
  const { report, setReport } = useReportStore();

  const title = section.group;

  // Keys of fields that can be selected in this section
  const availableFieldValues = section.fields.map(f => f.field);

  // All selected fields from all sections
  const allSelectedFields = report?.options?.select ?? [];

  const isAllSelected =
    availableFieldValues.length > 0 &&
    availableFieldValues.every(field =>
      (allSelectedFields as string[]).includes(field),
    );

  const matchingFields: Option[] = section.fields
    .map(o => ({
      value: o.field,
      label: o.label + (o.time_period_affected ? ' *' : ''),
    }))
    .filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  // The "All" option is only available when not searching
  const displayedFields: Option[] = search
    ? matchingFields
    : [ALL_OPTION, ...matchingFields];

  const handleSelectOption = (newOption: string) => {
    const cleanSelect = (values: string[]) =>
      values.filter(v => v !== ALL_OPTION.value);

    if (newOption === ALL_OPTION.value) {
      // Toggle every field of this section at once
      const select = isAllSelected
        ? allSelectedFields.filter(
            field => !availableFieldValues.includes(field),
          )
        : Array.from(new Set([...allSelectedFields, ...availableFieldValues]));

      setReport({
        options: {
          ...report?.options,
          select: cleanSelect(select),
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      return;
    }

    const isAlreadySelected = (allSelectedFields as string[]).includes(
      newOption,
    );

    setReport({
      options: {
        ...report?.options,
        select: cleanSelect(
          isAlreadySelected
            ? allSelectedFields.filter(field => field !== newOption)
            : Array.from(new Set([...allSelectedFields, newOption])),
        ),
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  };

  // Hide the whole section when none of its fields match the search
  if (!matchingFields.length) return null;

  return (
    <Box
      sx={{
        widtH: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        paddingY: '1rem',
      }}
    >
      <Typography variant='subtitle2'>{title}</Typography>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'transparent',
            borderRadius: '10px',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          outline: 'none',
          '&:focus': {
            outline: 'none',
          },
        }}
      >
        {displayedFields.map((item, index) => (
          <Box
            key={`${item.value}-${index}`}
            sx={{
              width: '33.3333%',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    item.value === ALL_OPTION.value
                      ? isAllSelected
                      : (allSelectedFields as string[]).includes(item.value)
                  }
                  onChange={() => handleSelectOption(item.value)}
                />
              }
              label={item.label}
              sx={{ maxWidth: '100%', wordBreak: 'break-all' }}
            />
          </Box>
        ))}
      </Box>
      {displayedFields.some(({ label }) => label.endsWith(' *')) && (
        <Typography variant='subtitle2'>
          * field affected by selected time period
        </Typography>
      )}
    </Box>
  );
};
